import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { updateSubmissionData, updateSubmissionDocuments } from 'apis/legitimizer/kyb/api';
import { KybSubmissionData, KybSubmissionDocument, DocumentType } from 'apis/legitimizer/types';
import { isDefined } from 'common/utils';

import BankDetailsFormFields from './BankDetailsFormFields';
import { SectionForm } from './SectionForm';
import { getBankDetailsSchema } from './validation';
import { KybProcessContext } from '../KybProcessContext';
import { SectionConfigurationContext } from '../SectionConfigurationContext';
import { bankAccountTypesForCountry } from '../countryCodes';
import { transformBankDetailsFormValuesToApi, transformKybSubmissionToBankDetailsFormValues } from '../transformers';
import { BankDetailsFormValues } from '../types';
import { useBankDetailsFieldsConfigMemo } from '../useFieldsConfigMemo';

type Props = {
  feedback?: string;
  submissionDocuments: KybSubmissionDocument[];
  submissionData: KybSubmissionData;
  onSubmitSuccess: (section: string, skipSection?: boolean) => Promise<void>;
};

const resolveDocuments = (documents: KybSubmissionDocument[], type: DocumentType) =>
  documents?.filter(document => document.documentType === type) || [];

const BankDetails = ({ feedback, submissionData, submissionDocuments, onSubmitSuccess }: Props) => {
  const intl = useIntl();
  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

  const sectionConfiguration = useContext(SectionConfigurationContext);
  const { countryCode, processId } = useContext(KybProcessContext);

  const [bankStatementDocumentIdList, setBankStatementDocumentIdList] = useState<number[]>([]);

  const bankDetailsFieldsConfig = useBankDetailsFieldsConfigMemo({ sectionConfiguration });

  const bankStatementDocuments = useMemo(() => {
    return resolveDocuments(submissionDocuments, 'BANK_STATEMENT');
  }, [submissionDocuments]);

  const hasExistingDocument = !!bankStatementDocuments.length;

  const bankDetailsFormSchema = getBankDetailsSchema(false, bankDetailsFieldsConfig, countryCode, hasExistingDocument);

  const initialFormValues = useMemo(() => {
    setBankStatementDocumentIdList(bankStatementDocuments.map(document => document.id));

    const values = transformKybSubmissionToBankDetailsFormValues(submissionData, submissionDocuments);
    if (!isDefined(values.accountType)) {
      values.accountType = bankAccountTypesForCountry(countryCode)?.shift()?.value;
    }
    return values;
  }, [bankStatementDocuments, countryCode, submissionData, submissionDocuments]);

  const handleSubmit = async (data: BankDetailsFormValues) => {
    const newDocuments = data.bankDocuments
      .map(documentWrapper => documentWrapper.file)
      // Array.filter doesn't narrow down the type but flatmap does.
      // https://github.com/microsoft/TypeScript/issues/16069#issuecomment-1234119963
      .flatMap(file => (!!file ? file : []));

    if (bankStatementDocumentIdList.length || newDocuments.length) {
      await updateSubmissionDocuments(processId, 'BANK_STATEMENT', {
        existingIds: bankStatementDocumentIdList,
        newDocuments,
      });
    }

    const submitData = {
      ...submissionData,
      bankAccounts: transformBankDetailsFormValuesToApi(data),
    };

    await updateSubmissionData(processId, submitData);

    await onSubmitSuccess(data.nextSection ? data.nextSection : 'bank-details', !!data.nextSection);
  };

  return (
    <SectionForm
      subtitle={t('compliance.kyb.sections.bank-details.subtitle')}
      initialFormValues={initialFormValues}
      schema={bankDetailsFormSchema}
      feedback={feedback}
      onSubmit={handleSubmit}
    >
      {({ submitting }) => (
        <BankDetailsFormFields
          countryCode={countryCode}
          disabled={submitting}
          config={{ bankDetails: bankDetailsFieldsConfig }}
          existingDocuments={bankStatementDocuments.filter(document =>
            bankStatementDocumentIdList.includes(document.id),
          )}
          setDocumentIdList={setBankStatementDocumentIdList}
        />
      )}
    </SectionForm>
  );
};

export default BankDetails;
